/* eslint-disable import/prefer-default-export */
import BaseFeature from '@appblocks/codegeneration/BaseFeature';
import ProjectConfiguration from '@appblocks/codegeneration/ProjectConfiguration';
import {
    CodeBlock,
    BlockType,
    SettingType,
    Define,
    ValidationMessageType,
    ValidationMessage,
    SettingState,
    Setting,
    TibboDataType,
} from '@appblocks/codegeneration/types';
import BaseNode from '@appblocks/codegeneration/Nodes/BaseNode';
import NodePort from '@appblocks/codegeneration/Nodes/NodePort';
import NodeParameter from '@appblocks/codegeneration/Nodes/NodeParameter';


export const getCodeBlocks = (feature: BaseNode): CodeBlock[] => {
    const blocks: CodeBlock[] = [];
    const project: ProjectConfiguration = feature.project;

    const DATATYPE = feature.getSettingValue('FUNCTIONCODE');
    const DEVICE = feature.getSettingValue('DEVICE');
    let PROTOCOL = 'modbus_RTU';
    let INTERFACE = feature.getSettingValue('SERIAL_PORT');
    const LENGTH = feature.getSettingValue('LENGTH');
    const links = project.getNodeLinks(feature, 'modbus_poll');
    if (links.length === 0) {
        return blocks;
    }
    const source = project.getFeature(links[0].getSettingValue('SOURCE_FEATURE')) as BaseNode;
    const sourceParams = source.getPort(links[0].getSettingValue('SOURCE_PORT'))?.params;
    let DEVICEID = project.parseCodeExpression(feature.getSettingValue('DEVICEID'), sourceParams, TibboDataType.BYTE);
    const ADDRESS = project.parseCodeExpression(feature.getSettingValue('REGISTER_ADDRESS'), sourceParams, TibboDataType.WORD);
    const DATA = project.parseCodeExpression(feature.getSettingValue('DATA'), sourceParams, TibboDataType.STRING);

    if (DEVICE !== '') {
        const device = project.getFeature(DEVICE);
        PROTOCOL = device?.getSettingValue('PROTOCOL');
        if (PROTOCOL === 'modbus_TCP') {
            INTERFACE = `${device?.getSettingValue('NAME')}_MODBUS_SOCK_NUM`;
        } else {
            const feature = project.getFeature(device?.getSettingValue('SERIAL_PORT'));
            if (feature) {
                INTERFACE = `SER_${feature.getSettingValue('NAME')}`;
            }
            DEVICEID = device?.getSettingValue('DEVICEID');
        }
    } else if (PROTOCOL === 'modbus_RTU') {
        const featureSerialPort = project.getFeature(feature.getSettingValue('SERIAL_PORT'));
        if (featureSerialPort) {
            INTERFACE = `SER_${featureSerialPort.getSettingValue('NAME')}`;
        }
    }


    blocks.push({
        location: 'GLOBAL_DECLARATIONS',
        blockType: BlockType.CODE,
        id: 'declare_modbus_request_count',
        template: `
declare modbus_request_count as word`,
    });

    blocks.push({
        location: 'GLOBAL_DIMS',
        blockType: BlockType.CODE,
        id: 'modbus_request_count',
        template: `
dim modbus_request_count as word=0`,
    });

    blocks.push({
        location: 'GLOBAL_DECLARATIONS',
        blockType: BlockType.CODE,
        id: `declare_modbus_request_${feature.getID()}_transaction_id`,
        template: `
declare modbus_request_${feature.getID()}_transaction_id as word
'declare modbus_request_${feature.getID()}_request_item as modbus_request_item`,
    });

    blocks.push({
        location: 'GLOBAL_DIMS',
        blockType: BlockType.CODE,
        id: `modbus_request_${feature.getID()}_transaction_id`,
        template: `
dim modbus_request_${feature.getID()}_transaction_id as word=0
'dim modbus_request_${feature.getID()}_request_item as modbus_request_item
`,
    });


    const doneLinks = project.getNodeLinks(feature, 'done');
    const doneBlock = doneLinks.length > 0 ? `'****${doneLinks[0].getSourceId()}****'` : '';

    const responseLinks = project.getNodeLinks(feature, 'modbus_response');
    const timeoutLinks = project.getNodeLinks(feature, 'modbus_timeout');

    if (DEVICE === '') {
        blocks.push({
            location: 'on_ser_data_arrival',
            blockType: BlockType.CODE,
            id: `MODBUS_RECV_${INTERFACE}`,
            template: `
if ser.num=${INTERFACE} then
    'modbus master data
    modbus_master_receive(ser.getdata(255),modbus_RTU)
end if`,
        });
    }

    for (let i = 0; i < links.length; i += 1) {
        const link = links[i];
        blocks.push({
            location: link.getSourceId(),
            blockType: BlockType.CODE,
            id: link.getID(),
            template: `
if modbus_queue_get_count() < MODBUS_MAX_REQUESTS then
    modbus_request_count = modbus_request_count + 1
    modbus_request_${feature.getID()}_transaction_id = modbus_request_count
    modbus_master_request(${DEVICEID}, ${DATATYPE}, ${ADDRESS}, ${LENGTH}, modbus_create_data(${DATATYPE},${DATA || '""'}), ${INTERFACE}, ${PROTOCOL},modbus_request_${feature.getID()}_transaction_id)
    
    
    'modbus_request_${feature.getID()}_request_item.device_id = ${DEVICEID}
    'modbus_request_${feature.getID()}_request_item.transactionId = modbus_request_${feature.getID()}_transaction_id
    'modbus_request_${feature.getID()}_request_item.start_register = ${ADDRESS}
    'modbus_request_${feature.getID()}_request_item.count = ${LENGTH}
    'modbus_request_${feature.getID()}_request_item.data = modbus_create_data(${DATATYPE},${DATA || '""'})
    'modbus_request_${feature.getID()}_request_item.interface_number = ${INTERFACE}
    'modbus_request_${feature.getID()}_request_item.protocol = ${PROTOCOL}
    'modbus_master_enqueue_request(${DEVICEID}, ${DATATYPE}, ${ADDRESS}, ${LENGTH}, modbus_create_data(${DATATYPE},${DATA || '""'}), ${INTERFACE}, ${PROTOCOL})
    
end if
${doneBlock}
`,
        });
    }

    if (responseLinks.length > 0) {
        const link = responseLinks[0];

        let dataType = 'modbus_float';
        if (feature.getSettingValue('VARTYPE') === 'INTEGER') {
            dataType = 'modbus_unsigned';
        } else if (feature.getSettingValue('VARTYPE') === 'SIGNED_INTEGER') {
            dataType = 'modbus_signed';
        }
        let endian = 'modbus_big';
        if (feature.getSettingValue('ENDIAN') === 'LITTLE') {
            endian = 'modbus_little';
        }

        blocks.push({
            location: 'callback_modbus_slave_reply',
            blockType: BlockType.CODE,
            template: `
if request_item.transactionId = modbus_request_${feature.getID()}_transaction_id then
'if request_item.device_id=modbus_request_${feature.getID()}_request_item.device_id 
'AND request_item.start_register=modbus_request_${feature.getID()}_request_item.start_register
'AND request_item.count=modbus_request_${feature.getID()}_request_item.count
'AND request_item.interface_number=modbus_request_${feature.getID()}_request_item.interface_number
'AND request_item.protocol=modbus_request_${feature.getID()}_request_item.protocol
'then
    dim response_data as float = modbus_parse_response(reply, ${dataType}, ${endian})
    if request_item.request_function=ReadCoilStatus OR request_item.request_function=ReadInputStatus then
        response_data=bytes(0) AND 1
    end if

    '****${link.getSourceId()}****'
end if
            `,
        });
    }

    if (timeoutLinks.length > 0) {
        const link = timeoutLinks[0];

        blocks.push({
            location: 'callback_modbus_slave_reply_timeout',
            blockType: BlockType.CODE,
            template: `
if request_item.transactionId = modbus_request_${feature.getID()}_transaction_id then
'if request_item.device_id=modbus_request_${feature.getID()}_request_item.device_id 
'AND request_item.start_register=modbus_request_${feature.getID()}_request_item.start_register
'AND request_item.count=modbus_request_${feature.getID()}_request_item.count
'AND request_item.interface_number=modbus_request_${feature.getID()}_request_item.interface_number
'AND request_item.protocol=modbus_request_${feature.getID()}_request_item.protocol
'then
    '****${link.getSourceId()}****'
end if
            `,
        });
    }

    return blocks;
};
